import { pino } from "pino";

type Logger = {
  debug: (...args: unknown[]) => void;
  info: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
};

// biome-ignore lint/suspicious/noEmptyBlockStatements: <documentation requirement>
const noop = () => {};

const isDev = typeof window !== "undefined" && window.location.hostname === "localhost";

const pinoLogger = isDev
  ? pino({
      browser: {
        asObject: true,
        // biome-ignore lint/suspicious/noExplicitAny: pino browser write object type is not exported well
        write(obj: any) {
          const { level, msg, time, logArgs, pid, hostname, name, v, ...rest } = obj;

          let label = "DEBUG";
          let color = "#8b5cf6"; // violet-500
          let icon = "🐛";
          let method = console.debug;

          if (level >= 50) {
            label = "ERROR";
            color = "#ef4444"; // red-500
            icon = "🚨";
            method = console.error;
          } else if (level >= 40) {
            label = "WARN";
            color = "#f59e0b"; // amber-500
            icon = "⚠️";
            method = console.warn;
          } else if (level >= 30) {
            label = "INFO";
            color = "#3b82f6"; // blue-500
            icon = "ℹ️";
            method = console.info;
          }

          const badgeStyle = `background: ${color}; color: white; padding: 2px 6px; border-radius: 4px; font-weight: bold; font-size: 10px;`;
          const timeStyle = "color: #9ca3af; font-size: 10px; font-weight: normal; margin-left: 6px;";
          const msgStyle = "font-weight: 600; color: inherit; margin-left: 6px;";

          const formattedTime = new Date(time || Date.now()).toLocaleTimeString("ru-RU", {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            fractionalSecondDigits: 3,
          });

          // Format: [ICON LABEL] Time Message
          const prefix = [`%c${icon} ${label}%c${formattedTime}%c${msg}`, badgeStyle, timeStyle, msgStyle];
          const args = logArgs || [];
          const logDetails = Object.keys(rest).length > 0 ? [rest] : [];

          method(...prefix, ...args, ...logDetails);
        },
      },
      level: import.meta.env.DEV ? "debug" : "info",
    })
  : null;

const logger: Logger =
  isDev && pinoLogger
    ? new Proxy(pinoLogger, {
        get(target, prop) {
          const key = prop as keyof Logger;
          if (["debug", "info", "warn", "error"].includes(key)) {
            // biome-ignore lint/suspicious/noExplicitAny: proxy generic handler
            return (...args: any[]) => {
              if (args.length >= 2 && typeof args[0] === "string") {
                // biome-ignore lint/suspicious/noExplicitAny: pino method dynamic access
                return (target as any)[key]({
                  msg: args[0],
                  logArgs: args.slice(1),
                });
              }
              // biome-ignore lint/suspicious/noExplicitAny: pino method dynamic access
              return (target as any)[key](...args);
            };
          }
          // biome-ignore lint/suspicious/noExplicitAny: pino prop access
          return (target as any)[prop];
        },
      })
    : { debug: noop, info: noop, warn: noop, error: noop };

export default logger;
